import { useQuery } from '@tanstack/react-query';
import { Alert, Spin } from 'antd';
import queryString from 'query-string';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { updateProps } from '../../contextRedux/Product/ProductModel';
import { setProducts } from '../../contextRedux/Product/ProductStore';
import ProductService from '../../services/product/ProductService';
import SystemInformationService from '../../services/systemInformation/SystemInformationService';
import { RootState } from '../../store';
import { setSystemInformations } from './../../contextRedux/SystemInformation/SystemInformationStore';
import ProductTable from './ProductTable/ProductTable';

const Product: React.FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const searchTerm = useSelector((state: RootState) => state.models?.product.props.searchTerm);
  const categoryId = useSelector((state: RootState) => state.models?.product.props.categoryId);
  const skip = useSelector((state: RootState) => state.models?.product.props.skip);
  const take = useSelector((state: RootState) => state.models?.product.props.take);
  const refresh = useSelector((state: RootState) => state.models?.product.props.refresh);

  const [isParamsInitialized, setIsParamsInitialized] = useState<boolean>(false);

  const previousParamsRef = useRef({ term: '', page: '', pageSize: '', category: '' });

  useEffect(() => {
    const queryParams = queryString.parse(location.search);

    const termInUrl = (queryParams.term as string) || '';
    const pageInUrl = Number(queryParams.page) || 1;
    const pageSizeInUrl = Number(queryParams.pageSize) || take;
    const categoryInUrl = Number(queryParams.category) || null;

    const calculatedSkip = (pageInUrl - 1) * pageSizeInUrl;

    dispatch(updateProps({ field: 'searchTerm', value: termInUrl }));
    dispatch(updateProps({ field: 'skip', value: calculatedSkip }));
    dispatch(updateProps({ field: 'take', value: pageSizeInUrl }));
    dispatch(updateProps({ field: 'categoryId', value: categoryInUrl }));

    previousParamsRef.current = {
      term: termInUrl,
      page: pageInUrl.toString(),
      pageSize: pageSizeInUrl.toString(),
      category: categoryInUrl?.toString() || '',
    };
    setIsParamsInitialized(true);
  }, [dispatch, location.search, take]);

  useEffect(() => {
    if (isParamsInitialized) {
      const queryParams = queryString.parse(location.search);
      const currentPage = Math.floor(skip / take) + 1;

      if (
        searchTerm !== previousParamsRef.current.term ||
        currentPage.toString() !== previousParamsRef.current.page ||
        take.toString() !== previousParamsRef.current.pageSize ||
        categoryId?.toString() !== previousParamsRef.current.category
      ) {
        const params = {
          ...queryParams,
          term: searchTerm || undefined,
          page: currentPage > 1 ? currentPage.toString() : undefined,
          pageSize: take !== 10 ? take.toString() : undefined,
          category: categoryId || undefined,
        };

        const newSearch = queryString.stringify(params);
        navigate(`${location.pathname}?${newSearch}`, { replace: true });

        previousParamsRef.current = {
          term: searchTerm || '',
          page: currentPage.toString(),
          pageSize: take.toString(),
          category: categoryId?.toString() || '',
        };
      }
    }
  }, [searchTerm, skip, take, categoryId, isParamsInitialized, navigate, location.pathname, location.search]);

  const {
    data: _dates,
    isFetching: isDevicesLoading,
    isError: isErrorOnFetchDevices,
    error: devicesError,
    refetch,
  } = useQuery({
    queryKey: ['products', searchTerm, categoryId, skip, take, refresh],
    queryFn: () =>
      ProductService.search({ termen: searchTerm || undefined, categoryId: categoryId || undefined, skip, take }).then(
        (data: any) => {
          dispatch(updateProps({ field: 'totalItems', value: data?.totalItems || 0 }));
          dispatch(setProducts(data?.response));
          return data?.response;
        }
      ),
    enabled: isParamsInitialized,
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const { data: _dsystemInformations, isFetching: isCategoriesLoading } = useQuery({
    queryKey: ['systemInformations'],
    queryFn: () =>
      SystemInformationService.search({}).then((data: any) => {
        dispatch(setSystemInformations(data?.response));
        return data?.response;
      }),
    refetchOnWindowFocus: false,
    staleTime: 0,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  useEffect(() => {
    if (isParamsInitialized && searchTerm === '') {
      refetch();
    }
  }, [searchTerm, isParamsInitialized, refetch]);

  if (isDevicesLoading || isCategoriesLoading) {
    return <Spin size='large' className='spinner' />;
  }

  if (isErrorOnFetchDevices) {
    return <Alert message={devicesError.message} type='error' />;
  }

  return (
    <>
      <h1 style={{ marginBottom: 50 }}>Catalog </h1>
      <ProductTable />
    </>
  );
};

export default Product;
